import { compact } from 'lodash';

interface TreeData {
  parentId: string;
  level: number;
  parentNames: string[];
}

export function flattenRecursiveTree<
  T extends { id: string; name?: string; children?: T[] | null },
>(tree: T[]): (Omit<T, 'children'> & TreeData)[] {
  const flatArray: (Omit<T, 'children'> & TreeData)[] = [];

  // Recursive function to traverse each node
  function traverse(
    node: T,
    parentId: string,
    level: number,
    parentNames: string[]
  ) {
    // Remove the 'children' property for flat structure
    const { children, ...nodeWithoutChildren } = node;
    flatArray.push({ ...nodeWithoutChildren, parentId, level, parentNames });
    if (children && children.length) {
      children
        // Sort children by name
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        .forEach((child) =>
          traverse(
            child,
            node.id,
            level + 1,
            compact([...parentNames, node.name])
          )
        );
    }
  }

  // Start traversal for each root element in the tree
  tree.forEach((root) => traverse(root, '', 0, []));
  return flatArray;
}

type TreeNode = { id: string; children?: TreeNode[] | null };

export function buildParentMap(
  data: TreeNode[],
  parents: string[] = []
): Record<string, string[]> {
  const result: Record<string, string[]> = {};

  data.forEach((node) => {
    if (!node.children) return;

    // For each child, record all parent IDs
    node.children.forEach((child) => {
      result[child.id] = [...parents, node.id];
    });
    // Recurse deeper
    Object.assign(result, buildParentMap(node.children, [...parents, node.id]));
  });

  return result;
}
